import { VaultSDK, type Vault as VaultHandle } from '@cef-ai/vault-sdk';
import type { CereWallet } from '@/lib/clients/cere-wallet/connect';
import { buildSigningSdk } from '@/lib/clients/vault/signing-sdk';
import { provisionAddProxy } from '@/lib/clients/vault/onboarding-provision';
import {
  getOnboardingStatusWithAction,
  postOnboardingBootstrap,
} from '@/lib/clients/ddc/onboarding-http';
import { runVaultOnboarding } from './run-onboarding';
import type { OnboardingFlowEvent } from './onboarding';
import { vaultHandleToRecord, type VaultRecord } from '@/lib/vault/types';
import { stepUpRequiredError } from '@/lib/vault/step-up-error';

export interface ClaimVaultArgs {
  wallet: CereWallet;
  vaultName: string;
  vaultBaseUrl: string;
  /** S3 gateway pubkey — the on-chain `addProxy` target of the onboarding
   *  switch's `add_proxy` step (the GATEWAY's proxy, distinct from the
   *  vault-api delegate the SDK grants inside `ensure`). No longer used to
   *  mint a delegation token: vault-sdk 5.0.0 claims tokenless. */
  gatewayPublicKey: string;
  /** S3 gateway base URL. Vault drives the onboarding switch itself
   *  (status / bootstrap / add_proxy / register_credential / none) instead
   *  of letting the SDK orchestrate, so the gateway URL is needed at
   *  this layer. */
  s3Endpoint: string;
  garUrl?: string;
  /** Cere chain WS RPC — required for the onboarding `add_proxy` step AND
   *  for `vault.ensure()`'s default path, which grants vault-api an on-chain
   *  NonTransfer proxy (`chainUrl` on the signing SDK) before claiming. */
  rpcEndpoint?: string;
  onProgress?: (event: string) => void;
}

export interface ClaimVaultResult {
  /** Plain-data record kept in React state / storage. */
  record: VaultRecord;
  /** Live SDK handle — use it for vault HTTP calls in the same render. */
  handle: VaultHandle;
  /** SDK instance — reuse for marketplace/health calls without rebuilding. */
  sdk: VaultSDK;
}

/**
 * Resolve or claim the user's vault.
 *
 * SHOULD be called from inside a user gesture (a click). Step 0 no longer needs
 * one itself — it is a synchronous read, not a ceremony — but the signatures in
 * steps 1 and 2 do, and on the `add_proxy` path they land 12-30s later, which
 * is the whole reason step 0 exists.
 *
 * 0. **Ceremony-surface gate.** `wallet.ceremonySurface()`, before anything
 *    that awaits the chain: `'popup'` means a later signature would need a
 *    `window.open` the browser will block, so we throw rather than start. See
 *    the block comment at the call site — the ordering is the fix, and see
 *    `docs/ceremony-surface-requirement.md` for the engines where this gate
 *    cannot fire.
 *
 * 1. **Onboarding switch.** GET /auth/onboarding/status and dispatch on
 *    `next_user_action`:
 *      - `"bootstrap" | "fund_wallet" | "seed_ddc_deposit"` → POST /auth/onboarding/bootstrap
 *      - `"add_proxy"`                  → submit proxy.addProxy(<gateway>) via the user's wallet
 *      - `"register_credential"`        → addProxy if `gateway_is_proxy=false`, otherwise terminal
 *      - `"none" | "ready"`             → onboarding complete
 *    The orchestrator loops until terminal; the iteration cap is the
 *    backstop against a misbehaving server that never advances.
 *    A 404 from /status means the gateway has ONBOARDING_ENABLED=false
 *    (production) — we skip the switch entirely and let the SDK proceed.
 *
 * 2. **Vault claim — tokenless, with the on-chain write-delegation grant.**
 *    `sdk.vault.ensure({ name, onboard: false })` on the default (wallet)
 *    path first grants vault-api an on-chain NonTransfer proxy via
 *    `ensureVaultDelegate` (emitting the `authorizing-vault` progress kind,
 *    and lazy-loading `@cef-ai/chain` in-browser for exactly that step),
 *    then claims with `{name, label: "vault"}` and NO delegation token —
 *    vault-api routes the claim through the proxy saga. The SDK retries the
 *    claim POST on 425 (grant still propagating) and retryable-503.
 *    `onboard: false` tells the SDK to trust that we've handled onboarding
 *    ourselves — it throws `OnboardingRequiredError` if the gateway
 *    disagrees, which we let bubble. A grant failure FAILS the claim (the
 *    SDK throws): the tokenless claim is useless without the delegate able
 *    to write, so there is no degraded "claimed but unauthorized" state to
 *    report any more.
 */
export async function claimVault(a: ClaimVaultArgs): Promise<ClaimVaultResult> {
  // Signing instance: drives the discrete signing ops (vault.ensure claim, and
  // GAR agreement inside agents.connect). Data-plane reads do NOT go through a
  // signer at all — callers build the Bearer instance from `./read-client.ts`
  // (`mintVaultReadClient` -> `DelegationAuthProvider`). This comment previously
  // said reads used the direct-sign `createVaultBrowserClient`, which stopped
  // being true when reads moved to Bearer.
  const sdk = buildSigningSdk(a.wallet, {
    vaultBaseUrl: a.vaultBaseUrl,
    s3Endpoint: a.s3Endpoint,
    ...(a.garUrl ? { garUrl: a.garUrl } : {}),
    ...(a.rpcEndpoint ? { chainUrl: a.rpcEndpoint } : {}),
  });

  // ===========================================================================
  // CEREMONY-SURFACE GATE. Load-bearing, and its POSITION — first, before
  // anything that can await the chain — is part of the fix, not a stylistic
  // choice. `tests/unit/vault/claim-ceremony-surface.test.ts` pins both.
  //
  // SECOND OF TWO GATES, and deliberately not the only one. `claimWithWallet`
  // (`src/routes/vault-layout.tsx`) checks the same thing before its
  // `startReadSdk` Bearer round-trip. That gate cannot replace this one:
  // `VaultConnectFlow.onClaimVault` reaches `claimVault` without passing
  // through it, and `claimVault` is a public export whose precondition must
  // hold for any caller. This one cannot replace that one either — by the time
  // we get here a round-trip has already happened. The duplicate costs one
  // synchronous property read.
  //
  // Why. `claimVault` runs from one click, and once the onboarding switch hits
  // `add_proxy` it awaits an on-chain `Proxy.add_proxy` that resolves only when
  // FINALIZED (~12-30s on 6s blocks). Everything after that wait signs with no
  // user gesture left: `sdk.vault.ensure()` at minimum — its in-SDK NonTransfer
  // grant step signs too. If a signature from here would open a POPUP, it is
  // blocked, and
  // the user is stranded mid-claim with the proxy already on chain. So we
  // refuse to start rather than discover it 30 seconds in.
  //
  // SUPERSEDES the `warmUp()` gate (issue #41). `warmUp()` was removed in
  // @cef-ai/wallet 3.0.0: it existed only to spend the one unavoidable step-up
  // popup at a moment we chose, and with the ceremony in-frame behind the
  // wallet's own Confirm button there is no popup to pre-spend. A cold
  // signature now prompts in place. `ceremonySurface()` answers the question
  // `warmUp()` was really being asked — "would a signature from here need a
  // gesture I no longer have?" — and answers it WITHOUT running a ceremony,
  // which is the whole gain: no passkey sheet, no popup, no user interaction
  // just to find out.
  //
  // NOT EQUIVALENT TO WHAT IT REPLACES, and weaker in a specific place.
  // `ceremonySurface()` reports 'popup' from two things: a popup TRANSPORT
  // (any non-https origin), and a recorded policy REFUSAL. Recognising a
  // refusal as a policy denial needs `document.featurePolicy`, which is
  // Chromium-only — so on Safari and Firefox it reports 'iframe' and never
  // changes its mind, and this gate cannot fire. For Safari 15.5-16.3 that is
  // a REGRESSION, not merely a lost improvement: `warmUp()` opened its popup
  // inside the click, where the browser allowed it, so the claim SUCCEEDED
  // there. Now the block lands after `add_proxy`. It needs a policy refusal to
  // bite as well, which in that band is a configuration fault (a missing or
  // wrong `allow` attribute, a restrictive top-level Permissions-Policy header,
  // a nested embed) rather than a version fact — so a correctly configured
  // deployment does not hit it. Tracked as wallet#77.
  //
  // A synchronous read, so unlike `warmUp()` it spends none of the activation
  // budget it is protecting.
  // ===========================================================================
  if (a.wallet.ceremonySurface() === 'popup') {
    // Thrown, not returned: `claimVault` hands back a record rather than
    // rendering, and both call sites (`claimWithWallet` in
    // `src/routes/vault-layout.tsx`, `onClaimVault` in
    // `src/components/VaultConnectFlow.tsx`) already funnel a throw through
    // `toDisplayError` into their error banner and drop back to a screen whose
    // primary control re-runs the claim from a fresh click. That restart is
    // safe because the claim is idempotent end to end — onboarding is driven by
    // the gateway's `next_user_action` and `vault.ensure` is idempotent — so a
    // second attempt skips an `add_proxy` that already finalized and has no
    // finalization wait left to outlive its gesture. Shared factory so this and
    // the `claimWithWallet` gate raise the byte-identical error.
    throw stepUpRequiredError();
  }

  await runVaultOnboarding({
    getStatus: () => getOnboardingStatusWithAction(a.s3Endpoint, a.wallet.signingSigner),
    bootstrap: () => postOnboardingBootstrap(a.s3Endpoint, a.wallet.signingSigner),
    addProxy: async () => {
      if (!a.rpcEndpoint) {
        throw new Error('onboarding: add_proxy requires a configured rpcEndpoint');
      }
      return provisionAddProxy({
        signer: a.wallet.signingSigner,
        rpcEndpoint: a.rpcEndpoint,
        gatewayPubKeyHex: a.gatewayPublicKey,
      });
    },
    ...(a.onProgress
      ? { onProgress: (e: OnboardingFlowEvent) => a.onProgress?.(e.kind) }
      : {}),
  });

  const handle = await sdk.vault.ensure({
    name: a.vaultName,
    onboard: false,
    onProgress: a.onProgress
      ? (e) => a.onProgress?.(e.kind)
      : undefined,
  });

  // The delegate authorization is no longer a separate post-claim step: the
  // `ensure()` above granted vault-api's NonTransfer proxy on-chain before
  // claiming (see step 2 in the header), and a failure there throws out of
  // `ensure` — failing the whole claim — rather than leaving a claimed vault
  // whose delegate cannot write. Out-of-claim re-granting (disconnected
  // banner, Settings) lives in `./vault-delegate.ts`.

  return { record: vaultHandleToRecord(handle), handle, sdk };
}

export function defaultVaultName(w: CereWallet): string {
  const seed = (w.name && w.name.trim())
    || (w.email && w.email.split('@')[0])
    || w.publicKeyHex.slice(2, 10);
  return seed.replace(/[^a-zA-Z0-9_-]/g, '-').toLowerCase() + '-vault';
}
